import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import "./PolicyWorkspace.css";

const list = (response) => response.data?.results || response.data || [];
const money = (value) => Number(value || 0).toFixed(2);
const upper = (value) => (value || "").toUpperCase();
const errorText = (error, fallback) =>
  error.response?.data?.detail ||
  error.response?.data?.message ||
  fallback;

const tabs = [
  ["overview", "Overview"],
  ["activity", "Policy activity"],
  ["members", "Member information"],
  ["benefits", "Policy benefits"],
  ["documents", "Policy documents"],
  ["claims", "Claims"],
  ["history", "History"],
];

export default function PolicyWorkspace() {
  const [policies, setPolicies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [listCount, setListCount] = useState(0);
  const searchTimer = useRef(null);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [statusBox, setStatusBox] = useState(false);
  const [claimBox, setClaimBox] = useState(false);
  const [documentBox, setDocumentBox] = useState(false);
  const [form, setForm] = useState({});
  const [eligibility, setEligibility] = useState(null);
  const [claimForm, setClaimForm] = useState({
    member_ids: [],
    incident_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [documentForm, setDocumentForm] = useState({
    document_type: "OTHER",
    title: "",
    member: "",
    file: null,
  });
  const [saving, setSaving] = useState(false);

  const loadPolicies = async ({
    query = search,
    append = false,
    url = null,
  } = {}) => {
    if (listLoading) return;
    setListLoading(true);
    try {
      const response = await axiosInstance.get(
        url || "policies/",
        url
          ? undefined
          : {
              params: {
                page_size: 50,
                ...(query.trim()
                  ? { search: query.trim() }
                  : {}),
              },
            }
      );
      const payload = response.data || {};
      const rows = payload.results || payload || [];
      setPolicies((current) =>
        append ? [...current, ...rows] : rows
      );
      setNextPageUrl(payload.next || null);
      setListCount(payload.count || rows.length);
      return rows;
    } finally {
      setListLoading(false);
    }
  };

  const load = async () => {
    await loadPolicies({ query: search, append: false });
  };

  const open = async (id, nextTab = null) => {
    const response = await axiosInstance.get(`policies/${id}/`);
    setSelected(response.data);
    setForm(response.data);
    if (nextTab) setTab(nextTab);
  };

  const generateOfficialDocument = async (type, language = "ENGLISH") => {
    if (!selected) return;
    setSaving(true);
    try {
      const response = await axiosInstance.post(
        `policy-maintenance/${selected.id}/documents/generated/${type}/`,
        { language },
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`${type.replaceAll("_", " ")} generated successfully.`);
      await open(selected.id, "documents");
    } catch (error) {
      let message = "Could not generate the official policy document.";
      if (error?.response?.data instanceof Blob) {
        try {
          const data = JSON.parse(await error.response.data.text());
          message = data.detail || (data.details || []).join(" ") || message;
        } catch (_) {}
      } else {
        message = errorText(error, message);
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };


  useEffect(() => {
    const initialise = async () => {
      const params = new URLSearchParams(window.location.search);
      const requestedPolicy = params.get("policy");
      const requestedSearch = params.get("search") || "";
      const editRequested = params.get("edit") === "1";

      if (requestedSearch) {
        setSearch(requestedSearch);
      }

      const data = await loadPolicies({
        query: requestedSearch,
        append: false,
      });

      if (requestedPolicy) {
        await open(requestedPolicy);
      } else if (data?.[0]) {
        await open(data[0].id);
      }

      if (editRequested) {
        setEditing(true);
      }
    };

    initialise().catch(() =>
      toast.error("Could not load policies.")
    );
  }, []);

  useEffect(() => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }

    searchTimer.current = setTimeout(() => {
      loadPolicies({
        query: search,
        append: false,
      }).catch(() =>
        toast.error("Could not search policies.")
      );
    }, 250);

    return () => {
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
    };
  }, [search]);

  const shown = policies;

  const groupedMembers = useMemo(() => {
    const members = selected?.members || [];
    return {
      holders: members.filter((m) =>
        ["POLICY HOLDER", "SPOUSE"].includes(m.member_type) &&
        m.status !== "DECEASED"
      ),
      children: members.filter(
        (m) => m.member_type === "CHILD" && m.status !== "DECEASED"
      ),
      dependants: members.filter(
        (m) => m.member_type === "OTHER DEPENDANT" && m.status !== "DECEASED"
      ),
      deceased: members.filter((m) => m.status === "DECEASED"),
    };
  }, [selected]);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await axiosInstance.patch(
        `policies/${selected.id}/update/`,
        form
      );
      toast.success("Policy details updated.");
      setEditing(false);
      await open(selected.id);
      await load();
    } catch (error) {
      toast.error(errorText(error, "Could not update policy."));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await axiosInstance.post(
        `policies/${selected.id}/status/`,
        {
          status: form.new_status,
          reason: form.status_reason,
          effective_date: form.status_effective_date,
        }
      );
      toast.success("Policy status updated.");
      setStatusBox(false);
      await open(selected.id);
      await load();
    } catch (error) {
      toast.error(errorText(error, "Could not change policy status."));
    } finally {
      setSaving(false);
    }
  };
  const openClaim = async () => {
    try {
      const response = await axiosInstance.get(
        `policies/${selected.id}/claim-eligibility/`
      );
      setEligibility(response.data);
      setClaimForm({
        member_ids: [],
        incident_date: new Date().toISOString().slice(0, 10),
        notes: "",
      });
      setClaimBox(true);
    } catch (error) {
      toast.error(errorText(error, "Could not check claim eligibility."));
    }
  };

  const toggleClaimMember = (memberId) => {
    setClaimForm((current) => ({
      ...current,
      member_ids: current.member_ids.includes(memberId)
        ? current.member_ids.filter((id) => id !== memberId)
        : [...current.member_ids, memberId],
    }));
  };

  const createClaims = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await axiosInstance.post(
        `policies/${selected.id}/create-claims/`,
        claimForm
      );
      const count = response.data.length;
      toast.success(
        `${count} draft claim${count === 1 ? "" : "s"} created.`
      );
      setClaimBox(false);
      await open(selected.id, "claims");
      await load();
    } catch (error) {
      toast.error(errorText(error, "Could not create claim."));
    } finally {
      setSaving(false);
    }
  };

  const uploadDocument = async (event) => {
    event.preventDefault();
    if (!documentForm.file) {
      toast.error("Select a file to upload.");
      return;
    }
    const payload = new FormData();
    payload.append("document_type", documentForm.document_type);
    payload.append("title", documentForm.title);
    if (documentForm.member) payload.append("member", documentForm.member);
    payload.append("file", documentForm.file);
    setSaving(true);
    try {
      await axiosInstance.post(
        `policies/${selected.id}/documents/`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Policy document uploaded.");
      setDocumentBox(false);
      setDocumentForm({
        document_type: "OTHER",
        title: "",
        member: "",
        file: null,
      });
      await open(selected.id, "documents");
    } catch (error) {
      toast.error(errorText(error, "Could not upload document."));
    } finally {
      setSaving(false);
    }
  };

  const deleteDocument = async (document) => {
    if (!window.confirm(`Permanently remove ${document.title}?`)) return;
    try {
      await axiosInstance.delete(`policies/documents/${document.id}/`);
      toast.success("Document removed.");
      await open(selected.id, "documents");
    } catch (error) {
      toast.error(errorText(error, "Could not remove document."));
    }
  };

  const MemberRegister = ({ title, items, empty }) => (
    <section className="policy-register">
      <header>
        <div>
          <h3>{title}</h3>
          <span>{items.length} record{items.length === 1 ? "" : "s"}</span>
        </div>
      </header>
      {items.length ? (
        <div className="policy-table">
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>National ID</th>
                <th>Date of birth</th>
                <th>Relationship</th>
                <th>Date joined</th>
                <th>Inception date</th>
                <th>Monthly premium</th>
                <th>Transport</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((member) => (
                <tr key={member.id}>
                  <td>
                    <strong>{member.full_name}</strong>
                    <small>{member.code}</small>
                  </td>
                  <td>{member.national_id || "—"}</td>
                  <td>
                    {member.date_of_birth || "—"}
                    <small>{member.calculated_age} years</small>
                  </td>
                  <td>{member.relationship || "—"}</td>
                  <td>{member.date_joined || "—"}</td>
                  <td>{member.inception_date || "—"}</td>
                  <td>{money(member.monthly_premium)}</td>
                  <td>{member.mourners_transport ? "YES" : "NO"}</td>
                  <td>{member.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="policy-empty-row">{empty}</div>
      )}
    </section>
  );

  return (
    <section className="policy-page">
      <header className="policy-page-header">
        <div>
          <span>ASSURANCE OPERATIONS</span>
          <h1>Policies</h1>
          <p>
            Review policy activity, members, benefits, documents,
            billing position and claims from one record.
          </p>
        </div>
      </header>

      <div className="policy-layout">
        <aside className="policy-list">
          <input
            placeholder="Search policy or holder"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div
            className="policy-list-scroll"
            onScroll={(event) => {
              const node = event.currentTarget;
              const nearBottom =
                node.scrollTop + node.clientHeight >=
                node.scrollHeight - 120;
              if (
                nearBottom &&
                nextPageUrl &&
                !listLoading
              ) {
                loadPolicies({
                  append: true,
                  url: nextPageUrl,
                }).catch(() =>
                  toast.error("Could not load more policies.")
                );
              }
            }}
          >
            {shown.map((policy) => (
              <button
                type="button"
                key={policy.id}
                className={`${selected?.id === policy.id ? "active" : ""} status-${String(policy.status || "").toLowerCase()}`}
                onClick={() => open(policy.id)}
              >
                <strong>{policy.code}</strong>
                <span>{policy.policyholder_name || "NO POLICYHOLDER"}</span>
                <small>{policy.plan_name} · {policy.status}</small>
                <b>{money(policy.balance)}</b>
              </button>
            ))}
            {listLoading && (
              <div className="policy-list-state">
                Loading policies…
              </div>
            )}
            {!listLoading && policies.length < listCount && (
              <div className="policy-list-state">
                Scroll for more · {policies.length} of {listCount}
              </div>
            )}
          </div>
        </aside>

        <main className="policy-workspace">
          {!selected ? (
            <div className="policy-empty">
              Select a policy to open its complete record.
            </div>
          ) : (
            <>
              <div className="policy-record-head">
                <div>
                  <span>POLICY RECORD</span>
                  <h2>{selected.code}</h2>
                  <p>{selected.policyholder_name}</p>
                </div>
                <div className="policy-record-actions">
                  <button type="button" className="secondary" onClick={() => setEditing(true)}>Edit selected</button>
                  <button
                    type="button"
                    onClick={() =>
                      window.location.assign(
                        `/policy-maintenance?policy=${selected.id}`
                      )
                    }
                  >
                    Open maintenance
                  </button>
                  <button type="button" className="secondary" onClick={openClaim}>Create claim</button>
                </div>
              </div>

              <div className="policy-summary">
                {[
                  ["Status", selected.status],
                  ["Plan", selected.plan_name],
                  ["Premium / month", money(selected.monthly_premium)],
                  ["Balance", money(selected.balance)],
                  ["Start date", selected.start_date],
                  ["Next bill", selected.next_bill_date || "—"],
                ].map(([label, value]) => (
                  <div key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>

              <nav className="policy-tabs">
                {tabs.map(([id, label]) => (
                  <button
                    type="button"
                    key={id}
                    className={tab === id ? "active" : ""}
                    onClick={() => setTab(id)}
                  >
                    {label}
                  </button>
                ))}
              </nav>

              <div className="policy-tab-content">
                {tab === "overview" && (
                  <section className="policy-card">
                    <div className="policy-section-head">
                      <div>
                        <h2>Policy information</h2>
                        <p>Core cover, ownership and payment arrangement.</p>
                      </div>
                      <button
                        type="button"
                        className="status-action"
                        onClick={() => setStatusBox(true)}
                      >
                        Change status
                      </button>
                    </div>
                    <div className="detail-grid">
                      {[
                        ["Policyholder", selected.policyholder_name],
                        ["Proposal", selected.proposal_code],
                        ["Branch", selected.branch_name],
                        ["Salesperson", selected.salesperson_name || "—"],
                        ["Policy type", selected.policy_type_name],
                        ["Payment mode", selected.payment_mode],
                        ["Payment tender", selected.payment_method_name || "—"],
                        ["On Ecosure", selected.on_ecosure ? "YES" : "NO"],
                      ].map(([label, value]) => (
                        <article key={label}>
                          <span>{label}</span>
                          <strong>{value || "—"}</strong>
                        </article>
                      ))}
                    </div>
                  </section>
                )}

                {tab === "activity" && (
                  <section className="policy-card">
                    <div className="policy-section-head">
                      <div>
                        <h2>Policy activity</h2>
                        <p>
                          Legacy activity indicators and payment history.
                        </p>
                      </div>
                    </div>
                    <div className="activity-grid">
                      <article>
                        <span>Running duration</span>
                        <strong>{selected.running_duration_days} days</strong>
                      </article>
                      <article>
                        <span>Outstanding balance</span>
                        <strong>{money(selected.balance)}</strong>
                      </article>
                      <article>
                        <span>Open premiums</span>
                        <strong>{selected.open_premiums_count}</strong>
                      </article>
                      <article>
                        <span>Last statement balance</span>
                        <strong>{money(selected.last_statement_balance)}</strong>
                      </article>
                      <article>
                        <span>Last statement date</span>
                        <strong>{selected.last_statement_date || "—"}</strong>
                      </article>
                    </div>
                    <h3 className="subheading">Payment history</h3>
                    <div className="policy-table activity-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Receipt</th>
                            <th>Receipt date</th>
                            <th>Bill</th>
                            <th>Bill date</th>
                            <th>Bill amount</th>
                            <th>Amount paid</th>
                            <th>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selected.receipts || []).length ? (
                            selected.receipts.map((receipt) => (
                              <tr key={receipt.id}>
                                <td>{receipt.code}</td>
                                <td>{receipt.receipt_date}</td>
                                <td>{receipt.bill_code || "—"}</td>
                                <td>{receipt.bill_date || "—"}</td>
                                <td>{money(receipt.bill_amount)}</td>
                                <td>{money(receipt.amount)}</td>
                                <td>{money(receipt.bill_balance)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="empty-cell">
                                No receipts have been posted to this policy yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {tab === "members" && (
                  <div className="member-registers">
                    <MemberRegister
                      title="Policyholder & spouse"
                      items={groupedMembers.holders}
                      empty="No policyholder or spouse record."
                    />
                    <MemberRegister
                      title="Children"
                      items={groupedMembers.children}
                      empty="No children are linked to this policy."
                    />
                    <MemberRegister
                      title="Other dependants"
                      items={groupedMembers.dependants}
                      empty="No other dependants are linked to this policy."
                    />
                    <MemberRegister
                      title="Deceased members"
                      items={groupedMembers.deceased}
                      empty="No deceased members are recorded."
                    />
                  </div>
                )}

                {tab === "benefits" && (
                  <section className="policy-card">
                    <div className="policy-section-head">
                      <div>
                        <h2>Policy benefits</h2>
                        <p>
                          Plan services and system-wide default benefits.
                        </p>
                      </div>
                      <div className="benefit-total">
                        <span>Total benefits cost</span>
                        <strong>{money(selected.total_benefits_cost)}</strong>
                      </div>
                    </div>
                    <div className="policy-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Benefit code</th>
                            <th>Description</th>
                            <th>Benefit type</th>
                            <th>Benefit cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selected.plan_benefits || []).map((benefit) => (
                            <tr key={`plan-${benefit.id}`}>
                              <td>{benefit.benefit_code}</td>
                              <td>{benefit.description}</td>
                              <td>ON PLAN SERVICE</td>
                              <td>{money(benefit.cost)}</td>
                            </tr>
                          ))}
                          {(selected.default_benefits || []).map((benefit) => (
                            <tr key={`default-${benefit.id}`}>
                              <td>{benefit.benefit_code}</td>
                              <td>{benefit.description}</td>
                              <td>DEFAULT</td>
                              <td>{money(benefit.cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {tab === "documents" && (
                  <section className="policy-card">
                    <div className="policy-section-head">
                      <div>
                        <h2>Policy documents</h2>
                        <p>Generate official policy documents and manage uploaded supporting files.</p>
                      </div>
                      <button type="button" onClick={() => setDocumentBox(true)}>
                        Add document
                      </button>
                    </div>
                    <div className="official-document-actions">
                      <button type="button" disabled={saving} onClick={() => generateOfficialDocument("CERTIFICATE")}>Generate certificate</button>
                      <button type="button" disabled={saving} onClick={() => generateOfficialDocument("CONFIRMATION")}>Generate confirmation</button>
                      <button type="button" disabled={saving} onClick={() => generateOfficialDocument("TERMS", "ENGLISH")}>English terms</button>
                      <button type="button" disabled={saving} onClick={() => generateOfficialDocument("TERMS", "NDEBELE")}>Ndebele terms</button>
                      <button type="button" disabled={saving} onClick={() => generateOfficialDocument("ID_CARD")}>Generate ID card</button>
                    </div>

                    <div className="generated-document-list">
                      <h3>Official generated documents</h3>
                      {(selected.generated_documents || []).length ? (
                        selected.generated_documents.map((document) => (
                          <article key={`generated-${document.id}`}>
                            <div>
                              <strong>{document.document_type.replaceAll("_", " ")}</strong>
                              <span>{document.reference} · {document.language}</span>
                              <small>{document.generated_at}</small>
                            </div>
                            {document.file_url ? (
                              <a href={document.file_url} target="_blank" rel="noreferrer">Open</a>
                            ) : null}
                          </article>
                        ))
                      ) : (
                        <div className="policy-empty-row">
                          No official policy documents have been generated yet. Use the buttons above to generate them.
                        </div>
                      )}
                    </div>

                    <div className="document-grid">
                      <h3>Uploaded supporting documents</h3>
                      {(selected.documents || []).length ? (
                        selected.documents.map((document) => (
                          <article key={document.id}>
                            <div className="document-mark">
                              {document.file?.toLowerCase().endsWith(".pdf")
                                ? "PDF"
                                : "FILE"}
                            </div>
                            <div>
                              <strong>{document.title}</strong>
                              <span>{document.document_type}</span>
                              <small>{document.uploaded_at}</small>
                            </div>
                            <div className="document-actions">
                              <a href={document.file_url} target="_blank" rel="noreferrer">
                                Open
                              </a>
                              <button
                                type="button"
                                className="danger-text"
                                onClick={() => deleteDocument(document)}
                              >
                                Remove
                              </button>
                            </div>
                          </article>
                        ))
                      ) : (
                        <div className="policy-empty-row">
                          No policy documents have been uploaded.
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {tab === "claims" && (
                  <section className="policy-card">
                    <div className="policy-section-head">
                      <div>
                        <h2>Policy claims</h2>
                        <p>
                          Claims created from this policy and its covered members.
                        </p>
                      </div>
                      <button type="button" onClick={openClaim}>
                        Create claim
                      </button>
                    </div>
                    <div className="policy-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Claim</th>
                            <th>Member</th>
                            <th>Claim date</th>
                            <th>Incident date</th>
                            <th>Cash allowance</th>
                            <th>Total claim cost</th>
                            <th>Benefits</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selected.claims || []).length ? (
                            selected.claims.map((claim) => (
                              <tr key={claim.id}>
                                <td>{claim.code}</td>
                                <td>
                                  <strong>{claim.deceased_member_name}</strong>
                                  <small>{claim.deceased_member_code}</small>
                                </td>
                                <td>{claim.claim_date}</td>
                                <td>{claim.incident_date}</td>
                                <td>{money(claim.pay_out)}</td>
                                <td>{money(claim.total_claim_cost)}</td>
                                <td>{claim.benefits?.length || 0}</td>
                                <td>{claim.status}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="empty-cell">
                                No claims have been created from this policy.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {tab === "history" && (
                  <section className="policy-card">
                    <div className="policy-section-head">
                      <div>
                        <h2>Policy history</h2>
                        <p>Auditable changes made to the policy record.</p>
                      </div>
                    </div>
                    <div className="timeline">
                      {selected.events?.length ? (
                        selected.events.map((event) => (
                          <article key={event.id}>
                            <span>{event.created_at}</span>
                            <strong>{event.event_type}</strong>
                            <p>{event.reason || "—"}</p>
                            <small>{event.created_by_name}</small>
                          </article>
                        ))
                      ) : (
                        <p>No policy changes have been recorded.</p>
                      )}
                    </div>
                  </section>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {editing && (
        <div className="policy-modal policy-edit-modal">
          <form onSubmit={save}>
            <header>
              <div>
                <span>POLICY MAINTENANCE</span>
                <h2>Edit selected policy</h2>
                <p>
                  Review the complete policy record. Protected insurance fields
                  are displayed but can only be changed through their dedicated
                  maintenance workflows.
                </p>
              </div>
              <button type="button" onClick={() => setEditing(false)}>×</button>
            </header>

            <div className="policy-edit-body">
              <section className="policy-edit-section">
                <div className="policy-edit-section-title">
                  <h3>Policy identity</h3>
                  <span>READ ONLY</span>
                </div>
                <div className="policy-edit-read-grid">
                  {[
                    ["Policy number", form.code],
                    ["Proposal", form.proposal_code],
                    ["Status", form.status],
                    ["Branch", form.branch_name],
                    ["Salesperson", form.salesperson_name],
                    ["Start date", form.start_date],
                  ].map(([label, value]) => (
                    <article key={label}>
                      <span>{label}</span>
                      <strong>{value || "—"}</strong>
                    </article>
                  ))}
                </div>
              </section>

              <section className="policy-edit-section">
                <div className="policy-edit-section-title">
                  <h3>Cover and billing structure</h3>
                  <span>PROTECTED</span>
                </div>
                <div className="policy-edit-read-grid">
                  {[
                    ["Plan", `${form.plan_code || ""} ${form.plan_name || ""}`.trim()],
                    ["Policy type", form.policy_type_name || form.policy_type],
                    ["Monthly premium", money(form.monthly_premium)],
                    ["Balance", money(form.balance)],
                    ["Payment method", form.payment_method_name || "—"],
                    ["On Ecosure", form.on_ecosure ? "YES" : "NO"],
                  ].map(([label, value]) => (
                    <article key={label}>
                      <span>{label}</span>
                      <strong>{value || "—"}</strong>
                    </article>
                  ))}
                </div>
                <p className="policy-edit-guidance">
                  Use Upgrade or Downgrade to change the plan or premium
                  structure. Use Policy Status Engine for status changes and
                  Members for covered-person changes.
                </p>
              </section>

              <section className="policy-edit-section">
                <div className="policy-edit-section-title">
                  <h3>Policyholder</h3>
                  <span>MEMBER RECORD</span>
                </div>
                <div className="policy-edit-read-grid">
                  {[
                    ["Name", form.policyholder_name],
                    ["Policy members", `${(form.members || []).length} covered record(s)`],
                    ["Active claims", `${(form.claims || []).filter((item) => !["REJECTED", "CANCELLED"].includes(item.status)).length}`],
                    ["Open premiums", form.open_premiums_count],
                  ].map(([label, value]) => (
                    <article key={label}>
                      <span>{label}</span>
                      <strong>{value ?? "—"}</strong>
                    </article>
                  ))}
                </div>
                <button
                  type="button"
                  className="secondary policy-edit-link"
                  onClick={() =>
                    window.location.assign(`/members?policy=${selected.id}`)
                  }
                >
                  Open member management
                </button>
              </section>

              <section className="policy-edit-section">
                <div className="policy-edit-section-title">
                  <h3>Payment arrangement</h3>
                  <span>EDITABLE</span>
                </div>
                <div className="form-grid policy-edit-fields">
                  <label>
                    Payment mode
                    <input
                      value={form.payment_mode || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payment_mode: upper(event.target.value),
                        })
                      }
                    />
                  </label>

                  <label>
                    On Ecosure
                    <select
                      value={form.on_ecosure ? "YES" : "NO"}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          on_ecosure: event.target.value === "YES",
                        })
                      }
                    >
                      <option value="NO">NO</option>
                      <option value="YES">YES</option>
                    </select>
                  </label>

                  <label>
                    Current payment method
                    <input
                      value={form.payment_method_name || "—"}
                      readOnly
                    />
                  </label>
                </div>
              </section>

              <section className="policy-edit-section">
                <div className="policy-edit-section-title">
                  <h3>Payer identity and contact</h3>
                  <span>EDITABLE</span>
                </div>
                <div className="form-grid policy-edit-fields">
                  <label>
                    Payer first name
                    <input
                      value={form.payer_first_name || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payer_first_name: upper(event.target.value),
                        })
                      }
                    />
                  </label>

                  <label>
                    Payer surname
                    <input
                      value={form.payer_surname || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payer_surname: upper(event.target.value),
                        })
                      }
                    />
                  </label>

                  <label>
                    Work number
                    <input
                      value={form.payer_work_number || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payer_work_number: event.target.value,
                        })
                      }
                    />
                  </label>

                  <label>
                    Cell number
                    <input
                      value={form.payer_phone || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payer_phone: event.target.value,
                        })
                      }
                    />
                  </label>

                  <label>
                    Date of birth
                    <input
                      type="date"
                      value={form.payer_date_of_birth || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payer_date_of_birth: event.target.value,
                        })
                      }
                    />
                  </label>

                  <label>
                    Gender
                    <select
                      value={form.payer_gender || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payer_gender: event.target.value,
                        })
                      }
                    >
                      <option value="">SELECT</option>
                      <option value="MALE">MALE</option>
                      <option value="FEMALE">FEMALE</option>
                      <option value="OTHER">OTHER</option>
                    </select>
                  </label>

                  <label>
                    National ID
                    <input
                      value={form.payer_national_id || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payer_national_id: upper(event.target.value),
                        })
                      }
                    />
                  </label>

                  <label>
                    Email
                    <input
                      type="email"
                      value={form.payer_email || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payer_email: event.target.value.toLowerCase(),
                        })
                      }
                    />
                  </label>
                </div>
              </section>

              <section className="policy-edit-section">
                <div className="policy-edit-section-title">
                  <h3>Payer banking details</h3>
                  <span>EDITABLE</span>
                </div>
                <div className="form-grid policy-edit-fields">
                  <label>
                    Bank
                    <input
                      value={form.payer_bank_name || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payer_bank_name: upper(event.target.value),
                        })
                      }
                    />
                  </label>

                  <label>
                    Bank branch
                    <input
                      value={form.payer_bank_branch || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payer_bank_branch: upper(event.target.value),
                        })
                      }
                    />
                  </label>

                  <label>
                    Account number
                    <input
                      value={form.payer_account_number || ""}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payer_account_number: upper(event.target.value),
                        })
                      }
                    />
                  </label>
                </div>
              </section>

              <section className="policy-edit-section policy-edit-reason">
                <div className="policy-edit-section-title">
                  <h3>Authorisation and audit</h3>
                  <span>REQUIRED</span>
                </div>
                <label>
                  Reason for change
                  <textarea
                    required
                    rows="3"
                    value={form.reason || ""}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        reason: upper(event.target.value),
                      })
                    }
                    placeholder="State exactly why this policy record is being updated"
                  />
                </label>
              </section>
            </div>

            <footer>
              <button
                type="button"
                className="secondary"
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
              <button disabled={saving}>
                {saving ? "Saving..." : "Save authorised changes"}
              </button>
            </footer>
          </form>
        </div>
      )}

      {statusBox && (
        <div className="policy-modal">
          <form onSubmit={changeStatus}>
            <header>
              <div>
                <span>POLICY LIFECYCLE</span>
                <h2>Change policy status</h2>
              </div>
              <button type="button" onClick={() => setStatusBox(false)}>×</button>
            </header>
            <div className="form-grid">
              <label>
                New status
                <select
                  required
                  value={form.new_status || ""}
                  onChange={(event) =>
                    setForm({ ...form, new_status: event.target.value })
                  }
                >
                  <option value="">SELECT</option>
                  <option>ACTIVE</option>
                  <option>SUSPENDED</option>
                  <option>INACTIVE</option>
                  <option>LAPSED</option>
                  <option>CANCELLED</option>
                  <option>MATURED</option>
                </select>
              </label>
              <label>
                Effective date
                <input
                  type="date"
                  value={form.status_effective_date || ""}
                  onChange={(event) =>
                    setForm({ ...form, status_effective_date: event.target.value })
                  }
                />
              </label>
              <label className="wide">
                Reason
                <textarea
                  required
                  value={form.status_reason || ""}
                  onChange={(event) =>
                    setForm({ ...form, status_reason: upper(event.target.value) })
                  }
                />
              </label>
            </div>
            <footer>
              <button type="button" className="secondary" onClick={() => setStatusBox(false)}>
                Cancel
              </button>
              <button disabled={saving}>
                {saving ? "Saving..." : "Confirm status"}
              </button>
            </footer>
          </form>
        </div>
      )}

      {claimBox && (
        <div className="policy-modal">
          <form onSubmit={createClaims} className="claim-modal">
            <header>
              <div>
                <span>CLAIM CREATION</span>
                <h2>Select deceased member(s)</h2>
              </div>
              <button type="button" onClick={() => setClaimBox(false)}>×</button>
            </header>
            <div className="claim-body">
              {eligibility?.policy_reasons?.length ? (
                <div className="claim-blocked">
                  <strong>Claim creation is blocked</strong>
                  {eligibility.policy_reasons.map((reason) => (
                    <p key={reason}>{reason}</p>
                  ))}
                </div>
              ) : (
                <div className="claim-ready">
                  Policy balance and status checks passed. Select one or more eligible members.
                </div>
              )}
              <label>
                Incident date
                <input
                  type="date"
                  value={claimForm.incident_date}
                  onChange={(event) =>
                    setClaimForm({ ...claimForm, incident_date: event.target.value })
                  }
                  required
                />
              </label>
              <div className="claim-member-list">
                {(eligibility?.members || []).map((member) => (
                  <label
                    key={member.id}
                    className={member.eligible ? "" : "disabled"}
                  >
                    <input
                      type="checkbox"
                      disabled={!eligibility.policy_eligible || !member.eligible}
                      checked={claimForm.member_ids.includes(member.id)}
                      onChange={() => toggleClaimMember(member.id)}
                    />
                    <span>
                      <strong>{member.full_name}</strong>
                      <small>
                        {member.code} · {member.member_type} · Inception {member.inception_date || "—"}
                      </small>
                      {!member.eligible &&
                        member.reasons.map((reason) => (
                          <em key={reason}>{reason}</em>
                        ))}
                    </span>
                  </label>
                ))}
              </div>
              <label>
                Notes
                <textarea
                  value={claimForm.notes}
                  onChange={(event) =>
                    setClaimForm({ ...claimForm, notes: upper(event.target.value) })
                  }
                />
              </label>
            </div>
            <footer>
              <button type="button" className="secondary" onClick={() => setClaimBox(false)}>
                Cancel
              </button>
              <button
                disabled={
                  saving ||
                  !eligibility?.policy_eligible ||
                  claimForm.member_ids.length === 0
                }
              >
                {saving ? "Creating..." : "Create draft claim(s)"}
              </button>
            </footer>
          </form>
        </div>
      )}

      {documentBox && (
        <div className="policy-modal">
          <form onSubmit={uploadDocument}>
            <header>
              <div>
                <span>POLICY DOCUMENTS</span>
                <h2>Add document</h2>
              </div>
              <button type="button" onClick={() => setDocumentBox(false)}>×</button>
            </header>
            <div className="form-grid">
              <label>
                Document type
                <select
                  value={documentForm.document_type}
                  onChange={(event) =>
                    setDocumentForm({ ...documentForm, document_type: event.target.value })
                  }
                >
                  <option>POLICY SCHEDULE</option>
                  <option>NATIONAL ID</option>
                  <option>BIRTH CERTIFICATE</option>
                  <option>MARRIAGE CERTIFICATE</option>
                  <option>BANK CONFIRMATION</option>
                  <option>CLAIM DOCUMENT</option>
                  <option>OTHER</option>
                </select>
              </label>
              <label>
                Linked member
                <select
                  value={documentForm.member}
                  onChange={(event) =>
                    setDocumentForm({ ...documentForm, member: event.target.value })
                  }
                >
                  <option value="">POLICY LEVEL</option>
                  {(selected?.members || []).map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.full_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="wide">
                Document title
                <input
                  required
                  value={documentForm.title}
                  onChange={(event) =>
                    setDocumentForm({ ...documentForm, title: upper(event.target.value) })
                  }
                />
              </label>
              <label className="wide">
                File
                <input
                  type="file"
                  required
                  onChange={(event) =>
                    setDocumentForm({
                      ...documentForm,
                      file: event.target.files?.[0] || null,
                    })
                  }
                />
              </label>
            </div>
            <footer>
              <button type="button" className="secondary" onClick={() => setDocumentBox(false)}>
                Cancel
              </button>
              <button disabled={saving}>
                {saving ? "Uploading..." : "Upload document"}
              </button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
}
